import React, { useEffect, useRef, useState } from 'react';
import {
  LikertScaleAnswer,
  MultipleChoiceAnswer,
  QuestionType,
  SurveyQuestion,
  SurveyResponseAnswer,
} from '@x-guard/xgac-types/xgac';
import { useSearchParams } from 'react-router-dom';
import { useLocale } from './lib/hook';
import './index.scss';
import './styles/colors.scss';
import {
  Alert, AnswerType, FinishSurveyDisplay, Header, LoadingScreen, Question,
} from './components';
import { getSurveyQuestions, updateSurveyResponses } from './lib/api';

const isMultipleChoiceAnswer = (answer: SurveyResponseAnswer): answer is MultipleChoiceAnswer => {

  return answer && typeof answer === 'object' && 'type' in answer && answer.type === QuestionType.MultipleChoice;

};

const isLikertScaleAnswer = (answer: SurveyResponseAnswer): answer is LikertScaleAnswer => {

  return answer && typeof answer === 'object' && 'type' in answer && answer.type === QuestionType.LikertScale;

};

const App: React.FC = () => {

  const { t } = useLocale();
  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [headerQuestionIndex, setHeaderQuestionIndex] = useState(1);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [search] = useSearchParams();
  const token = search.get('token');
  const [option, setOption] = useState(search.get('option'));
  const isFollowUp = useRef(false);
  const answers = useRef<SurveyResponseAnswer[]>([]);
  const [maxQuestions, setMaxQuestions] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorFound, setErrorFound] = useState(false);
  const [isBhvkTheme, setIsBhvkTheme] = useState(false);

  useEffect(() => {

    if (!token) {

      setErrorFound(true);
      setLoading(false);

    }

  }, [token]);

  useEffect(() => {

    const fetchData = async () => {

      try {

        if (!token) {

          return;

        }

        const data = await getSurveyQuestions(token);
        const hasFollowUp = data.questions.some((q: SurveyQuestion) => q.type === QuestionType.FollowUp);
        setQuestions(data.questions);
        setIsBhvkTheme(data.isBhvk);
        setMaxQuestions(hasFollowUp ? data.questions.length - 1 : data.questions.length);
        setLoading(false);

      } catch (error) {

        setErrorFound(true);
        setLoading(false);

      }

    };
    fetchData();

  }, [token, t]);

  const navigateToNextQuestion = async (answer: SurveyResponseAnswer, isCompleted?: boolean, skipResponse?: boolean) => {

    if (!skipResponse) {

      const existingAnswerIndex = answers.current.findIndex((a) => a.question._id === answer.question._id);
      if (existingAnswerIndex !== -1) {

        answers.current[existingAnswerIndex] = answer;

      } else {

        answers.current.push(answer);

      }

    }

    try {

      if (token) {

        if (isCompleted) {

          setIsPopupVisible(true);
          await updateSurveyResponses(token, answers.current, isCompleted);
          return;

        }

        await updateSurveyResponses(token, answers.current);

      }

    } catch (error) {

      setAlertMessage(t('component.alert.error'));
      return;

    }

    const nextQuestion = questions[currentQuestionIndex + 1];

    if ((isMultipleChoiceAnswer(answer) && answer.answer.value === 0)
      || (isLikertScaleAnswer(answer) && (answer.answer.value === 0 || answer.answer.value === 1))) {

      isFollowUp.current = true;

    } else {

      isFollowUp.current = questions[currentQuestionIndex].type === QuestionType.FollowUp;

    }

    if (nextQuestion && nextQuestion.type === QuestionType.FollowUp) {

      if (isFollowUp.current) {

        setCurrentQuestionIndex((prevIndex) => prevIndex + 1);

      } else {

        setCurrentQuestionIndex((prevIndex) => prevIndex + 2);
        setHeaderQuestionIndex((prevIndex) => prevIndex + 1);

      }

    } else {

      setCurrentQuestionIndex((prevIndex) => prevIndex + 1);
      setHeaderQuestionIndex((prevIndex) => prevIndex + 1);

    }

  };

  const previousQuestion = () => {

    if (questions[currentQuestionIndex].type === QuestionType.FollowUp && isFollowUp.current) {

      setCurrentQuestionIndex((prevIndex) => prevIndex - 1);

    } else if (currentQuestionIndex > 0) {

      if (questions[currentQuestionIndex - 1].type === QuestionType.FollowUp && !isFollowUp.current) {

        setCurrentQuestionIndex((prevIndex) => prevIndex - 2);
        setHeaderQuestionIndex((prevIndex) => prevIndex - 1);

      } else {

        setCurrentQuestionIndex((prevIndex) => prevIndex - 1);
        setHeaderQuestionIndex((prevIndex) => prevIndex - 1);

      }

    }

  };

  if (loading) {

    return (
      <div className="app">
        <Header current={headerQuestionIndex} max={maxQuestions} hideProgressBar={true} isBhvkLogo={isBhvkTheme}/>
        <LoadingScreen textMain={t('component.loading.loading')}/>
      </div>
    );

  }

  if (errorFound) {

    return (
      <div className="app">
        <Header current={headerQuestionIndex} max={maxQuestions} hideProgressBar={true} isBhvkLogo={isBhvkTheme}/>
        <LoadingScreen textMain={t('component.loading.cannotOpen')} textSecondary={t('component.loading.openSurveyAgain')}/>
      </div>
    );

  }

  if (option && currentQuestionIndex === 0) {

    const selectedQuestion = questions[0];
    if (selectedQuestion.options) {

      const selectedAnswer = selectedQuestion.options.find((ans) => ans.value === Number(option));
      if (selectedAnswer) {

        if (selectedQuestion.type === QuestionType.MultipleChoice || selectedQuestion.type === QuestionType.LikertScale) {

          navigateToNextQuestion({
            question: { _id: selectedQuestion._id, _ref: 'SurveyQuestion' },
            answer: { value: selectedAnswer.value, label: selectedAnswer.label },
            type: selectedQuestion.type,
          });

        }

      }

    }
    setOption(null);

  }

  return (
    <div className={`app ${isBhvkTheme ? 'bhvk-theme' : ''}`}>
      <Header current={headerQuestionIndex} max={maxQuestions} isBhvkLogo={isBhvkTheme}/>
      <Question
        current={headerQuestionIndex}
        total={maxQuestions}
        question={questions[currentQuestionIndex].question}
        previousQuestion={previousQuestion}
        isOptional={questions[currentQuestionIndex].type === QuestionType.OpenEnded}
      />
      {alertMessage && <Alert message={alertMessage} onClose={() => setAlertMessage(null)}/>}
      <AnswerType question={questions[currentQuestionIndex]} nextQuestion={navigateToNextQuestion}/>
      <FinishSurveyDisplay isVisible={isPopupVisible} isBhvkTheme={isBhvkTheme}/>
    </div>
  );

};

export default App;
